<script setup lang="ts">
import Purchase from '@/components/Purchase.vue'
import PurchaseQuick from '@/components/PurchaseQuick.vue'
import PurchaseStarterKit from '@/components/PurchaseStarterKit.vue'
import Modal from '@/components/Modal.vue'
import * as PurchaseService from '@/services/purchase'
import * as CartService from '@/services/cart'
import { track } from '@/services/analytics'
import type { Product } from '@/lib/types/Product'
import { onMounted, ref } from 'vue'
import type { WorkState } from '@/lib/types/WorkState'
import type { PurchaseData } from '@/lib/types/PurchaseData'
import type { Splide } from '@splidejs/splide'
import Toast from '@/components/Toast.vue'
import Button from '@/components/Button.vue'

const user = PurchaseService.getUser()
const savedState = PurchaseService.getSavedState() as
  | PurchaseData
  | Record<string, never>
const purchaseState = ref<WorkState>('IDLE')
const showNextOrderToast = ref(false)

const props = defineProps<{
  product: Product
  imageCarousel?: Splide
  neverPromptLogin?: boolean
  immediateCheckout?: boolean
}>()

const showFloatingPurchase = ref(false)

const onPurchase = (data: PurchaseData) => {
  purchaseState.value = 'IN_PROGRESS'
  let purchasePromise
  if (data.when === 'now') {
    purchasePromise = CartService.add(data as CartService.PayloadAddToCart)
  } else {
    purchasePromise = PurchaseService.createSubscription(data)
  }

  purchasePromise
    .then(() => {
      if (props.immediateCheckout) {
        CartService.checkout()
      } else {
        purchaseState.value = 'COMPLETE'
        if (data.when === 'next') {
          showNextOrderToast.value = true
        }
        showFloatingPurchase.value = false
        setTimeout(() => {
          purchaseState.value = 'IDLE'
        }, 3000)
      }
    })
    .catch((err) => {
      purchaseState.value = 'ERROR'
      window.Bugsnag.notify(err)
    })

  track(data.when === 'now' ? 'add_to_cart' : 'add_to_order', {
    product_id: data.id,
    variant_id: data.selectedVariantId,
    quantity: data.quantity,
    context: 'product_page',
    ...(data.selectedSellingPlanId
      ? {
          is_subscription: true,
        }
      : { is_subscription: false }),
  })
}

const onVariantChange = (variantId: string) => {
  if (props.imageCarousel) {
    const carouselEl = document.querySelector(
      `[data-image-carousel-container] [data-variant-id="${variantId}"]`,
    )
    if (carouselEl) {
      const index = [].findIndex.call(
        carouselEl?.parentElement?.children,
        (item) => item === carouselEl,
      )
      props.imageCarousel.go(index)
    }
  }
}

const plasticWasteSaving = ref<
  | {
      sku: string
      waste_saved_g: number
    }
  | undefined
>(undefined)

const loadPlasticWasteSaving = async () => {
  try {
    const res = await fetch(
      `https://api.bowercollective.com/plastic-waste/waste-saved-by-sku-12-month/${
        props.product.variants[props.product.selectedVariantId].sku
      }`,
    )
    plasticWasteSaving.value = await res.json()
  } catch (e) {
    window.Bugsnag.notify(e as Error)
  }
}

onMounted(async () => {
  try {
    loadPlasticWasteSaving()
  } catch (e) {
    window.Bugsnag.notify(e as Error)
  }
})

const purchaseFixedCtaEl = ref<HTMLElement | null>(null)

onMounted(() => {
  setupScrollSpy()
})

const setupScrollSpy = () => {
  let scrollY = 0

  if (purchaseFixedCtaEl.value) {
    setInterval(() => {
      if (purchaseFixedCtaEl.value) {
        if (scrollY > 1000) {
          purchaseFixedCtaEl.value.classList.add('is-showing')
        } else {
          purchaseFixedCtaEl.value.classList.remove('is-showing')
        }
      }
    }, 500)

    const onUpdate = () => {
      scrollY = window.scrollY
    }

    document.addEventListener('scroll', onUpdate, { passive: true })
    document.addEventListener('resize', onUpdate, { passive: true })
  }
}

const pathname = window.location.pathname

let isV2ProductTemplate = false
document.body.classList.forEach((cls) => {
  if (cls.startsWith('template-v2')) {
    isV2ProductTemplate = true
  }
})
</script>

<template>
  <div>
    <component
      :is="
        product.starterKitSubscriptionConfig !== null
          ? PurchaseStarterKit
          : Purchase
      "
      :product="product"
      :user="user"
      :login-link="`/account/login?return_url=${encodeURIComponent(pathname)}`"
      customer-portal-link="/account"
      next-order-link="/account"
      :purchase-state="purchaseState"
      :initial-selling-plan="savedState.selectedSellingPlanId"
      :initial-quantity="savedState.quantity"
      :initial-variant-id="savedState.selectedVariantId"
      :immediate-checkout="immediateCheckout"
      @purchase="onPurchase"
      @interaction="purchaseState = 'IDLE'"
      @variant-change="onVariantChange"
      :plastic-waste-saving="plasticWasteSaving"
    />
    <Teleport to="body" v-if="isV2ProductTemplate">
      <div class="PurchaseFixedCta" ref="purchaseFixedCtaEl">
        <div class="Stack Stack--small">
          <strong>{{ product.title }}</strong>
          <Button :modifiers="['block']" @click="showFloatingPurchase = true">
            Buy now
          </Button>
        </div>
      </div>
      <Modal v-if="showFloatingPurchase" @close="showFloatingPurchase = false">
        <PurchaseQuick
          :product="product"
          :user="user"
          :loginLink="`/account/login?return_url=${encodeURIComponent(pathname)}`"
          customer-portal-link="/account"
          next-order-link="/account"
          :purchase-state="purchaseState"
          :initial-when="user.subscriptions.length > 0 ? 'next' : 'now'"
          @purchase="onPurchase"
          @interaction="purchaseState = 'IDLE'"
          :show-link="false"
        />
      </Modal>
    </Teleport>
    <Toast
      v-if="showNextOrderToast"
      :mode="['success']"
      :has-close="true"
      @close="showNextOrderToast = false"
    >
      We’ve added this to your next order!
      <a href="/account">View your next order</a>
    </Toast>
  </div>
</template>

<style>
.Purchase--in-page {
  /* this lets it be a scroll target without bumping the top of the screen */
  margin-top: -100px;
  padding-top: 100px;

  @media (--mq-landscape-tablet-and-up) {
    margin-left: auto;
    margin-right: auto;
    width: 95%;
    max-width: 1144px;
  }
}

.PurchaseFixedCta {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: var(--grid-gap) calc(4 * var(--grid-gap)) var(--grid-gap)
    var(--grid-gap);
  background-color: var(--color-white);
  z-index: 10;
  transform: translateY(100%);
  transition: transform 0.3s ease;

  &.is-showing {
    transform: translateY(0);
  }

  @media (--mq-landscape-tablet-and-up) {
    display: none;
  }
}
</style>
